import { ApiClientImplementation } from 'infra/api-client/api-client'

import { PrepareApiRequestDecorator } from 'main/decorators/prepare-api-request-decorator/prepare-api-request-decorator'
import { makeLocalStorageAdapter } from 'main/factories/cache/local-storage-adapter-factory'
import { ApiRepository } from 'repository/api-repository'
import { PatientRepository } from 'repository/repositories/patient/patient-repository'
import { RemoteInvitePatient } from 'service/usecases/invite-patient/remote-invite-patient'
import PatientService from 'presentation/contexts/services/patient-service'
import { RemoteLoadPatientByCpf } from 'service/usecases/load-patient-by-cpf/remote-load-patient-by-cpf'
import { RemoteSearchPatient } from 'service/usecases/search-patient/remote-search-patient'
import { RemoteLoadPatientInfo } from 'service/usecases/load-patient-info/remote-load-patient-info'
import { RemoteLoadPatientDocuments } from 'service/usecases/load-patient-documents/remote-load-patient-documents'
import { RemoteDownloadPatientDocument } from 'service/usecases/download-patient-document/remote-download-patient-document'
import { RemoteLoadPatientHideContact } from 'service/usecases/load-patient-hide-contact/remote-load-patient-hide-contact'
import { RemoteLoadPatientPossibleMotherName } from 'service/usecases/load-patient-possible-mother-name/remote-load-patient-possible-mother-name'
import { RemoteValidatePatientMotherAndBirthday } from 'service/usecases/validate-patient-mother-and-birthday/remote-validate-patient-mother-and-birthday'
import { RemoteValidateRegisteredPatientInDb } from 'service/usecases/validate-registered-patient-in-db/remote-validate-registered-patient-in-db'
import { RemoteInvitePatientFromMv } from 'service/usecases/invite-patient-from-mv/invite-patient-from-mv'
import { RemoteLoadPatientPendencies } from 'service/usecases/load-patient-pendencies/remote-load-patient-pendencies'
import { RemoteLoadPatientSchedules } from 'service/usecases/load-patient-schedules/remote-load-patient-schedules'
import { RemoteInviteNewBornPatient } from 'service/usecases/invite-newborn-patient/remote-invite-newborn-patient'
import { RemoteGetPatientNextAttendances } from 'service/usecases/get-patient-next-attendances/remote-get-patient-next-attendances'
import { RemoteUpdatePatientById } from 'service/usecases/update-patient-by-id/remote-update-patient-by-id'
import { RemoteCheckPatientByEmail } from 'service/usecases/check-patient-by-email/check-patient-by-email'
import { RemoteCheckPatientByPhone } from 'service/usecases/check-patient-by-phone/check-patient-by-phone'
import { RemoteCheckPatientByCpf } from 'service/usecases/check-patient-by-cpf/check-patient-by-cpf'
import { RemoteUpdatePatientInfo } from 'service/usecases/update-patient-info/remote-update-patient-info'
import { RemoteAddPatientNew } from 'service/usecases/add-patient-new/remote-add-patient-new'
import { RemoteGetPatientByCpfOrName } from 'service/usecases/get-patient-by-cpf-or-name/remote-get-patient-by-cpf-or-name'
import { RemoteUpdatePatient } from 'service/usecases/update-patient/remote-update-patient'
import { RemoteGetPatients } from 'service/usecases/get-patients/remote-get-patients'
import { PatientNewRepository } from 'repository/repositories/patient-new/patient-new-repository'
import { ApiPatientImplementation } from 'infra/api-patient/api-patient'
import { PrepareApiRequestPatientDecorator } from 'main/decorators/prepare-api-request-patient-decorator/prepare-api-request-patient-decorator'
import { ApiPatientRepository } from 'repository/api-patient-repository'
import { RemoteUpdatePatientContact } from 'service/usecases/update-patient-contact/remote-update-patient-contact'
import { RemoteGetHealthInsuranceByPatient } from 'service/usecases/get-health-insurance-by-patient/remote-update-patient-contact'
import { RemoteGetAllHealthPlans } from 'service/usecases/get-all-health-plans/get-all-health-plans'
import { RemoteCreateHealthInsuranceByPatient } from 'service/usecases/create-health-insurance-by-patient/create-health-insurance-by-patient'
import { RemoteCreateNewPatient } from 'service/usecases/create-new-patient/remote-create-new-patient'
import { RemoteGetHealthSubPlans } from 'service/usecases/get-health-sub-plans/get-health-sub-plans'

export default function makePatientService() {
  const apiRepository = new PrepareApiRequestDecorator(
    makeLocalStorageAdapter(),
    new ApiRepository(new ApiClientImplementation())
  )

  const apiPatientNewRepository = new PrepareApiRequestPatientDecorator(
    makeLocalStorageAdapter(),
    new ApiPatientRepository(new ApiPatientImplementation())
  )

  const patientRepository = new PatientRepository(apiRepository)

  const patientNewRepository = new PatientNewRepository(apiPatientNewRepository)

  const invitePatient = new RemoteInvitePatient(patientRepository)
  const loadPatientByCpf = new RemoteLoadPatientByCpf(patientRepository)
  const loadPatientInfo = new RemoteLoadPatientInfo(patientRepository)
  const searchPatient = new RemoteSearchPatient(patientRepository)
  const loadPatientDocuments = new RemoteLoadPatientDocuments(patientRepository)
  const loadPatientHideContact = new RemoteLoadPatientHideContact(
    patientRepository
  )
  const downloadPatientDocument = new RemoteDownloadPatientDocument(
    patientRepository
  )
  const loadPatientPossibleMotherName = new RemoteLoadPatientPossibleMotherName(
    patientRepository
  )
  const validatePatientMotherAndBirthday =
    new RemoteValidatePatientMotherAndBirthday(patientRepository)

  const validateRegisteredPatientInDb = new RemoteValidateRegisteredPatientInDb(
    patientRepository
  )

  const checkPatientByEmail = new RemoteCheckPatientByEmail(patientRepository)

  const checkPatientByPhone = new RemoteCheckPatientByPhone(patientRepository)

  const checkPatientByCpf = new RemoteCheckPatientByCpf(patientRepository)

  const invitePatientFromMv = new RemoteInvitePatientFromMv(patientRepository)
  const loadPatientPendencies = new RemoteLoadPatientPendencies(
    patientRepository
  )
  const loadPatientSchedules = new RemoteLoadPatientSchedules(patientRepository)
  const inviteNewBornPatient = new RemoteInviteNewBornPatient(patientRepository)

  const getPatientNextAttendances = new RemoteGetPatientNextAttendances(
    patientRepository
  )

  const updatePatientInfo = new RemoteUpdatePatientInfo(patientRepository)

  const updatePatientById = new RemoteUpdatePatientById(patientRepository)
  const addPatientNew = new RemoteAddPatientNew(patientRepository)

  const getPatientByCpfOrName = new RemoteGetPatientByCpfOrName(
    patientRepository
  )
  const updatePatient = new RemoteUpdatePatient(patientRepository)

  const createNewPatient = new RemoteCreateNewPatient(patientNewRepository)
  const getPatients = new RemoteGetPatients(patientNewRepository)
  const updatePatientContact = new RemoteUpdatePatientContact(
    patientNewRepository
  )
  const getHealthInsuranceByPatient = new RemoteGetHealthInsuranceByPatient(
    patientNewRepository
  )

  const getAllHealthPlans = new RemoteGetAllHealthPlans(patientNewRepository)

  const createHealthInsuranceByPatient =
    new RemoteCreateHealthInsuranceByPatient(patientNewRepository)

  const getHealthSubPlans = new RemoteGetHealthSubPlans(patientNewRepository)

  return new PatientService({
    invitePatient,
    invitePatientFromMv,
    loadPatientByCpf,
    searchPatient,
    loadPatientInfo,
    loadPatientDocuments,
    downloadPatientDocument,
    loadPatientHideContact,
    loadPatientPossibleMotherName,
    validatePatientMotherAndBirthday,
    validateRegisteredPatientInDb,
    loadPatientPendencies,
    loadPatientSchedules,
    inviteNewBornPatient,
    getPatientNextAttendances,
    updatePatientById,
    checkPatientByEmail,
    checkPatientByPhone,
    checkPatientByCpf,
    updatePatientInfo,
    addPatientNew,
    getPatientByCpfOrName,
    updatePatient,
    createNewPatient,
    getPatients,
    updatePatientContact,
    getHealthInsuranceByPatient,
    getAllHealthPlans,
    createHealthInsuranceByPatient,
    getHealthSubPlans
  })
}
